import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Landing from "@/pages/Landing";
import Pricing from "@/pages/Pricing";
import Legal from "@/pages/Legal";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import Inbox from "@/pages/Inbox";
import Subscriptions from "@/pages/Subscriptions";
import Rules from "@/pages/Rules";
import Analytics from "@/pages/Analytics";
import Settings from "@/pages/Settings";
import PaymentSuccess from "@/pages/PaymentSuccess";
import EmbedCounter from "@/pages/EmbedCounter";
import OAuthProviderCallback from "@/pages/OAuthCallback";
import AppShell from "@/components/AppShell";
import CookieBanner from "@/components/CookieBanner";
import PaidRoute from "@/components/PaidRoute";
import AccessGate from "@/components/AccessGate";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center font-mono text-sm">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return <AppShell>{children}</AppShell>;
}

function AppRouter() {
  const location = useLocation();
  // Synchronous check: if returning from Google OAuth with session_id in fragment
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/privacy" element={<Navigate to="/legal/privacy_policy" replace />} />
      <Route path="/terms" element={<Navigate to="/legal/terms_and_conditions" replace />} />
      <Route path="/refunds" element={<Navigate to="/legal/refunds_policy" replace />} />
      <Route path="/legal" element={<Legal />} />
      <Route path="/legal/:slug" element={<Legal />} />
      <Route path="/embed/counter" element={<EmbedCounter />} />
      <Route path="/oauth/google/callback" element={<ProtectedRoute><OAuthProviderCallback provider="gmail" /></ProtectedRoute>} />
      <Route path="/oauth/microsoft/callback" element={<ProtectedRoute><OAuthProviderCallback provider="outlook" /></ProtectedRoute>} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/payment/success" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/inbox" element={<ProtectedRoute><AccessGate requireCapability="bulk_delete"><Inbox /></AccessGate></ProtectedRoute>} />
      <Route path="/subscriptions" element={<ProtectedRoute><AccessGate requireCapability="subscription_management"><Subscriptions /></AccessGate></ProtectedRoute>} />
      <Route path="/rules" element={<ProtectedRoute><AccessGate requireCapability="auto_rules"><Rules /></AccessGate></ProtectedRoute>} />
      <Route path="/analytics" element={<ProtectedRoute><AccessGate requireCapability="analytics"><Analytics /></AccessGate></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster position="bottom-right" toastOptions={{ style: { borderRadius: 0, border: "1px solid hsl(214 32% 91%)" } }} />
          <CookieBanner />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
